/**
 * User Management — admin only.
 * List / create / edit role / reset password / deactivate / delete users.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import {
    Plus, Search, KeyRound, Edit3, Trash2, ShieldCheck, AlertCircle, Copy, Check,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_LABELS = {
    admin: { label: "Administrador", color: "bg-zentra-primary text-white border-zentra-primary" },
    coordinator: { label: "Coordinador", color: "bg-sky-100 text-sky-900 border-sky-200" },
    supervisor: { label: "Supervisor", color: "bg-violet-100 text-violet-900 border-violet-200" },
    cleaner: { label: "Operario limpieza", color: "bg-emerald-100 text-emerald-900 border-emerald-300" },
    technician: { label: "Técnico", color: "bg-amber-100 text-amber-900 border-amber-300" },
    warehouse: { label: "Almacén", color: "bg-orange-100 text-orange-900 border-orange-300" },
    client: { label: "Cliente (portal)", color: "bg-rose-100 text-rose-900 border-rose-300" },
};

const EMPTY_CREATE = {
    email: "",
    name: "",
    role: "cleaner",
    password: "",
    client_id: "",
    employee_id: "",
    active: true,
};

export default function Users() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [createForm, setCreateForm] = useState(EMPTY_CREATE);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState(null); // {email, password}
    const [copied, setCopied] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get("/users"),
            api.get("/employees"),
            api.get("/clients"),
        ])
            .then(([u, e, c]) => {
                setUsers(u.data || []);
                setEmployees(e.data || []);
                setClients(c.data || []);
            })
            .catch((err) => setError(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);
    useEffect(load, [load]);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return users.filter((u) => {
            if (roleFilter && u.role !== roleFilter) return false;
            if (!s) return true;
            return (
                (u.email || "").toLowerCase().includes(s) ||
                (u.name || "").toLowerCase().includes(s)
            );
        });
    }, [users, search, roleFilter]);

    const handleCreate = async () => {
        if (!createForm.email.trim() || !createForm.name.trim()) {
            alert("Email y nombre son obligatorios");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                email: createForm.email.trim().toLowerCase(),
                name: createForm.name.trim(),
                role: createForm.role,
                password: createForm.password.trim() || null,
                client_id: createForm.role === "client" ? (createForm.client_id || null) : null,
                employee_id: createForm.employee_id || null,
                active: createForm.active,
            };
            const { data } = await api.post("/users", payload);
            if (data.generated_password) {
                setGeneratedPassword({ email: data.user.email, password: data.generated_password });
            }
            setShowCreate(false);
            setCreateForm(EMPTY_CREATE);
            load();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const openEdit = (u) => {
        setEditing(u);
        setEditForm({
            name: u.name || "",
            role: u.role,
            client_id: u.client_id || "",
            employee_id: u.employee_id || "",
            active: u.active !== false,
        });
    };

    const handleUpdate = async () => {
        setSaving(true);
        try {
            const payload = {
                name: editForm.name.trim(),
                role: editForm.role,
                client_id: editForm.role === "client" ? (editForm.client_id || null) : null,
                employee_id: editForm.employee_id || null,
                active: editForm.active,
            };
            await api.patch(`/users/${editing.id}`, payload);
            setEditing(null);
            load();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const handleResetPassword = async (u) => {
        const newPwd = window.prompt(
            `Resetear contraseña de ${u.email}.\nDeja vacío para generar una aleatoria, o escribe la nueva (mín. 6 caracteres):`,
            "",
        );
        if (newPwd === null) return; // cancelled
        try {
            const { data } = await api.post(`/users/${u.id}/password`, {
                new_password: newPwd.trim() || null,
            });
            setGeneratedPassword({ email: u.email, password: data.new_password });
        } catch (err) {
            alert(formatApiError(err));
        }
    };

    const handleDelete = async (u) => {
        if (u.id === currentUser?.id) {
            alert("No puedes eliminarte a ti mismo.");
            return;
        }
        if (!window.confirm(`¿Eliminar definitivamente al usuario ${u.email}?`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            load();
        } catch (err) {
            alert(formatApiError(err));
        }
    };

    const copyPassword = async () => {
        if (!generatedPassword) return;
        try {
            await navigator.clipboard.writeText(generatedPassword.password);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            alert("No se pudo copiar al portapapeles");
        }
    };

    if (loading) return <div className="zentra-skeleton h-72" data-testid="users-loading" />;

    return (
        <div className="space-y-5" data-testid="users-page">
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-900 inline-flex items-center gap-2">
                    <AlertCircle size={14} /> {error}
                </div>
            )}

            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-[10px] font-semibold tracking-widest text-zentra-accent-strong uppercase">
                        Seguridad y accesos
                    </p>
                    <h1 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1 inline-flex items-center gap-2">
                        <ShieldCheck size={24} /> Usuarios
                    </h1>
                    <p className="text-sm text-zentra-muted mt-1 max-w-2xl">
                        Gestiona accesos: crea cuentas, restablece contraseñas, cambia roles. Cada
                        usuario inicia sesión con su email y contraseña.
                    </p>
                </div>
                <button
                    onClick={() => setShowCreate(true)}
                    className="zentra-btn-primary"
                    data-testid="users-create-btn"
                >
                    <Plus size={16} /> Nuevo usuario
                </button>
            </div>

            {/* Filters */}
            <div className="flex gap-2 flex-wrap items-center">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zentra-muted" />
                    <input
                        className="zentra-input pl-9"
                        placeholder="Buscar por nombre o email…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="users-search"
                    />
                </div>
                <select
                    className="zentra-input max-w-[200px]"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    data-testid="users-role-filter"
                >
                    <option value="">Todos los roles</option>
                    {Object.entries(ROLE_LABELS).map(([key, val]) => (
                        <option key={key} value={key}>{val.label}</option>
                    ))}
                </select>
                <span className="text-xs text-zentra-muted ml-auto">
                    {filtered.length} / {users.length} usuarios
                </span>
            </div>

            {/* Table */}
            <div className="zentra-card-static overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="users-table">
                        <thead className="bg-zentra-bg/60 border-b border-zentra-border text-[10px] uppercase tracking-widest text-zentra-muted">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Usuario</th>
                                <th className="text-left px-4 py-3 font-semibold">Rol</th>
                                <th className="text-left px-4 py-3 font-semibold">Empleado / Cliente</th>
                                <th className="text-left px-4 py-3 font-semibold">Estado</th>
                                <th className="text-right px-4 py-3 font-semibold">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((u) => {
                                const role = ROLE_LABELS[u.role] || { label: u.role, color: "bg-zinc-100 border-zinc-300" };
                                const linkedEmployee = employees.find((e) => e.id === u.employee_id);
                                const linkedClient = clients.find((c) => c.id === u.client_id);
                                const isSelf = u.id === currentUser?.id;
                                return (
                                    <tr key={u.id} className="border-b border-zentra-border last:border-b-0 hover:bg-zentra-bg/40" data-testid={`user-row-${u.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-zentra-ink flex items-center gap-2">
                                                {u.name}
                                                {isSelf && (
                                                    <span className="text-[9px] font-bold tracking-wider uppercase text-zentra-accent-strong border border-zentra-accent/30 bg-zentra-accent/10 px-1.5 py-0.5 rounded">Tú</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-zentra-muted font-mono-zentra">{u.email}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${role.color}`}>
                                                {role.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-zentra-ink">
                                            {linkedEmployee && <div>{linkedEmployee.name}</div>}
                                            {linkedClient && <div className="text-zentra-muted">{linkedClient.name}</div>}
                                            {!linkedEmployee && !linkedClient && <span className="text-zentra-muted">—</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            {u.active === false ? (
                                                <span className="inline-flex text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border bg-zinc-200 text-zinc-700 border-zinc-300">Inactivo</span>
                                            ) : (
                                                <span className="inline-flex text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border bg-emerald-100 text-emerald-900 border-emerald-300">Activo</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <button onClick={() => handleResetPassword(u)} className="zentra-btn-ghost text-xs" data-testid={`user-reset-${u.id}`}>
                                                <KeyRound size={13} /> Contraseña
                                            </button>
                                            <button onClick={() => openEdit(u)} className="zentra-btn-ghost text-xs ml-1" data-testid={`user-edit-${u.id}`}>
                                                <Edit3 size={13} /> Editar
                                            </button>
                                            {!isSelf && (
                                                <button onClick={() => handleDelete(u)} className="zentra-btn-ghost text-xs ml-1 text-red-700 hover:bg-red-50" data-testid={`user-delete-${u.id}`}>
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr><td colSpan={5} className="text-center text-zentra-muted py-12 text-sm">Sin resultados.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create modal */}
            <Modal
                open={showCreate}
                onClose={() => { setShowCreate(false); setCreateForm(EMPTY_CREATE); }}
                title="Nuevo usuario"
                subtitle="Crea una cuenta de acceso. Si dejas la contraseña vacía, se generará una automáticamente."
                wide
                testId="user-create-modal"
                footer={
                    <>
                        <button onClick={() => { setShowCreate(false); setCreateForm(EMPTY_CREATE); }} className="zentra-btn-ghost" data-testid="user-create-cancel">Cancelar</button>
                        <button onClick={handleCreate} disabled={saving} className="zentra-btn-primary" data-testid="user-create-save">
                            {saving ? "Creando…" : "Crear usuario"}
                        </button>
                    </>
                }
            >
                <UserForm form={createForm} setForm={setCreateForm} employees={employees} clients={clients} isCreate />
            </Modal>

            {/* Edit modal */}
            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={editing ? `Editar ${editing.email}` : ""}
                wide
                testId="user-edit-modal"
                footer={
                    <>
                        <button onClick={() => setEditing(null)} className="zentra-btn-ghost" data-testid="user-edit-cancel">Cancelar</button>
                        <button onClick={handleUpdate} disabled={saving} className="zentra-btn-primary" data-testid="user-edit-save">
                            {saving ? "Guardando…" : "Guardar"}
                        </button>
                    </>
                }
            >
                {editing && (
                    <UserForm form={editForm} setForm={setEditForm} employees={employees} clients={clients} isCreate={false} />
                )}
            </Modal>

            {/* Generated password reveal modal */}
            <Modal
                open={!!generatedPassword}
                onClose={() => setGeneratedPassword(null)}
                title="Credenciales generadas"
                subtitle="Copia y comparte estas credenciales con el usuario. No volverán a mostrarse."
                testId="user-pwd-modal"
                footer={
                    <button onClick={() => setGeneratedPassword(null)} className="zentra-btn-primary" data-testid="user-pwd-close">Hecho</button>
                }
            >
                {generatedPassword && (
                    <div className="space-y-3">
                        <div>
                            <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">Email</p>
                            <p className="font-mono-zentra text-sm text-zentra-ink mt-1">{generatedPassword.email}</p>
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">Contraseña</p>
                            <div className="mt-1 flex items-center gap-2">
                                <code className="flex-1 font-mono-zentra text-base bg-zentra-bg border border-zentra-border rounded-md px-3 py-2">
                                    {generatedPassword.password}
                                </code>
                                <button onClick={copyPassword} className="zentra-btn-ghost" data-testid="user-pwd-copy">
                                    {copied ? <Check size={14} className="text-emerald-700" /> : <Copy size={14} />} {copied ? "Copiado" : "Copiar"}
                                </button>
                            </div>
                        </div>
                        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 inline-flex items-start gap-2">
                            <AlertCircle size={14} className="mt-0.5 shrink-0" />
                            <span>Recomienda al usuario cambiar su contraseña tras el primer inicio de sesión.</span>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

function UserForm({ form, setForm, employees, clients, isCreate }) {
    return (
        <div className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="zentra-label">Email</label>
                    <input
                        type="email"
                        className="zentra-input"
                        value={form.email || ""}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="nombre@valmay.com"
                        disabled={!isCreate}
                        data-testid="user-form-email"
                    />
                </div>
                <div>
                    <label className="zentra-label">Nombre completo</label>
                    <input
                        className="zentra-input"
                        value={form.name || ""}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Juan Pérez"
                        data-testid="user-form-name"
                    />
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="zentra-label">Rol</label>
                    <select
                        className="zentra-input"
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        data-testid="user-form-role"
                    >
                        {Object.entries(ROLE_LABELS).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                        ))}
                    </select>
                </div>
                {form.role === "client" ? (
                    <div>
                        <label className="zentra-label">Cliente vinculado</label>
                        <select
                            className="zentra-input"
                            value={form.client_id || ""}
                            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                            data-testid="user-form-client"
                        >
                            <option value="">— Seleccionar —</option>
                            {clients.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div>
                        <label className="zentra-label">Empleado vinculado (opcional)</label>
                        <select
                            className="zentra-input"
                            value={form.employee_id || ""}
                            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                            data-testid="user-form-employee"
                        >
                            <option value="">— Sin vincular —</option>
                            {employees.map((emp) => (
                                <option key={emp.id} value={emp.id}>{emp.name} · {emp.role}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
            {isCreate && (
                <div>
                    <label className="zentra-label">Contraseña inicial</label>
                    <input
                        type="text"
                        className="zentra-input font-mono-zentra"
                        value={form.password || ""}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder="Dejar vacío para generar automáticamente"
                        data-testid="user-form-password"
                    />
                    <p className="text-[11px] text-zentra-muted mt-1">Mínimo 6 caracteres. Si la dejas vacía, se generará una aleatoria de 10 caracteres.</p>
                </div>
            )}
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer pt-2">
                <input
                    type="checkbox"
                    checked={form.active !== false}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    data-testid="user-form-active"
                />
                Cuenta activa
            </label>
        </div>
    );
}
